import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'access_token';

// Configure axios defaults
axios.defaults.baseURL = process.env.REACT_APP_BACKEND_URL;

// Axios interceptor pour ajouter le token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Logout function
  const logout = useCallback(() => {
    console.log('[AUTH] Logout - clearing all auth data');
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('user'); // Clear any cached user data
    localStorage.removeItem('trackvibes_user'); // Clear any other potential cache
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Init auth au mount
  useEffect(() => {
    const initAuth = async () => {
      console.log('[AUTH] Init starting...');
      const storedToken = localStorage.getItem(TOKEN_KEY);
      console.log('[AUTH] Stored token exists:', !!storedToken);
      
      if (!storedToken) {
        console.log('[AUTH] No token, setting loading false');
        setLoading(false);
        return;
      }

      // Token existe, configurer axios et vérifier avec /me
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      
      try {
        console.log('[AUTH] Calling /auth/me...');
        const response = await axios.get(`${API}/auth/me`);
        console.log('[AUTH] /me success:', response.data?.email);
        console.log('[AUTH] /me user data:', {
          is_pro_lifetime: response.data?.is_pro_lifetime,
          pro_status: response.data?.pro_status,
          plan: response.data?.plan,
          pro_effective: response.data?.pro_effective
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('[AUTH] /me failed:', error.response?.status);
        // Token invalide - nettoyer SEULEMENT si 401
        if (error.response?.status === 401) {
          console.log('[AUTH] Token invalid (401), cleaning up');
          logout();
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [logout]);

  // Login function
  const login = async (email, password) => {
    console.log('[AUTH] Login attempt for:', email);
    setAuthError(null);
    
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const data = response.data;
    
    // Accepter access_token OU token (fallback)
    const accessToken = data.access_token || data.token;
    
    console.log('[AUTH] Response received, token exists:', !!accessToken);
    
    if (!accessToken) {
      // Pas de token = erreur
      const errorMsg = data.message || data.detail || 'Connexion impossible';
      console.error('[AUTH] No token in response:', errorMsg);
      setAuthError(errorMsg);
      throw new Error(errorMsg);
    }
    
    // Token reçu - stocker et configurer
    console.log('[AUTH] Storing token, length:', accessToken.length);
    localStorage.setItem(TOKEN_KEY, accessToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    
    setToken(accessToken);
    
    // Log user data from login response
    console.log('[AUTH] User from login response:', {
      email: data.user?.email,
      is_pro_lifetime: data.user?.is_pro_lifetime,
      pro_status: data.user?.pro_status,
      plan: data.user?.plan
    });
    
    setUser(data.user);
    setIsAuthenticated(true);
    
    // Force a fresh fetch from /auth/me to ensure we have latest data
    try {
      const meResponse = await axios.get(`${API}/auth/me`);
      console.log('[AUTH] Fresh user from /me:', {
        email: meResponse.data?.email,
        is_pro_lifetime: meResponse.data?.is_pro_lifetime,
        pro_status: meResponse.data?.pro_status
      });
      setUser(meResponse.data);
    } catch (e) {
      console.warn('[AUTH] Could not refresh user from /me:', e);
    }
    
    // Vérifier stockage
    const stored = localStorage.getItem(TOKEN_KEY);
    console.log('[AUTH] Token stored successfully:', !!stored);
    
    return data.user;
  };

  // Register function
  const register = async (email, password, dj_name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, dj_name });
    return response.data;
  };

  // Resend verification email
  const resendVerification = async (email) => {
    const response = await axios.post(`${API}/auth/resend-verification`, { email });
    return response.data;
  };

  // Get auth header
  const getAuthHeader = useCallback(() => {
    const t = token || localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, [token]);

  // Check if user has PRO access
  const isPro = useCallback(() => {
    if (!user) return false;
    return (
      user.pro_effective === true ||
      user.pro_effective === 'true' ||
      user.pro_status === 'active' ||
      user.pro_status === 'trialing' ||
      user.pro_status === 'lifetime' ||
      user.is_pro_lifetime === true ||
      user.is_pro_lifetime === 'true'
    );
  }, [user]);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('[AUTH] Refresh user failed:', error);
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      isAuthenticated,
      authError,
      login, 
      register, 
      logout, 
      resendVerification,
      getAuthHeader, 
      setUser, 
      setToken,
      setIsAuthenticated,
      isPro,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
